import React, {createContext, useContext} from 'react';

import { Formik } from 'formik';
import Yup from 'yup';
import CacheService from 'service/CacheService';
import OrderAPI from 'service/api/OrderAPI';

import {SubmitButton, RGForm} from 'views/FormElements';
import {A, Separator, Img} from 'views/AppElements';
import UserAvatar from 'views/user/UserAvatar';
import PriceLabel from 'views/product/PriceLabel';
import ProductUtil from 'util/ProductUtil';

import AppConstants from 'AppConstants';
import AppUtil from 'AppUtil';
import OrderFormLoader from 'views/order/OrderFormLoader';
import ProductSocialShare from 'views/product/ProductSocialShare';
import OrderProductImage from 'views/order/OrderProductImage';

import LocationTypeInput, {AddressInput} from 'views/order/input/LocationTypeInput';
import CouponCodeInput from 'views/order/input/CouponCodeInput';
import OrderConfirmation from 'views/order/OrderConfirmation';
import PaymentPreferenceInput from 'views/order/input/PaymentPreferenceInput';

import {PaymentOptions} from 'views/order/PaymentInfo';

export const OrderContext = createContext();

const DELIVERY_TYPE = AppConstants.DELIVERY_TYPE;

const ProductInfo = ({showCreator}) => {
    const {product, values, productSizes} = useContext(OrderContext);

    let productSizeOptions = values.productSizeMetaData || productSizes;
    const perPrice = product.price + (values.productSize? productSizeOptions[values.productSize].priceAddition: 0);

    let creatorURL = null, createrName = null;
    if(showCreator) {
        creatorURL = AppUtil.getDesignerURL(product.creator);
        createrName = product.creator? AppUtil.getCreaterName(product.creator): null;
    }

    return <React.Fragment>
        <h1 className='flexColumn productTitle'>
            <span>{product.title}</span>
            {
                product.productMetaData &&
                <small>- {product.productMetaData}</small>
            }
        </h1>

        {product.creator && showCreator === true &&
            <div className="flexRow" style={{alignItems: "center"}}>
                <A href={creatorURL}><UserAvatar user={product.creator} /></A>
                    <span style={{color: '#5b5b5b', fontSize: '90%'}}>from <A href={creatorURL} style={{color: '#f8931b', textTransform: 'capitalize', fontWeight: 'bold'}}>
                            {createrName}
                    </A>
                    </span>
            </div>
        }

        <PriceLabel style={{margin: '10px 0', fontWeight: 'bold', fontSize: '26px', display: 'block'}} 
                        price={perPrice} 
                        aliasPrice={product.aliasPrice}
                        productType={product.productType} invertColor={true} discountFont="65%" />



        {product.description &&
            <span style={{display: 'block', color: 'gray', margin: '15px 0', fontSize: '110%', whiteSpace: 'pre-line'}}>- {product.description}</span>	
        }
    </React.Fragment>
}

export const TotalCost = ({showPriceOnly}) => {
    const {product, values, status, productSizes} = useContext(OrderContext);

    let discountAmount = 0;
    if(values.hasCouponCode && status && status.discountCoupon && status.discountCoupon.couponCode === values.couponCode)
        discountAmount = status.discountCoupon.discountAmount * values.quantity;

    let productSizeOptions = values.productSizeMetaData || productSizes;
    
    
    let totalPrice;
    if(values.isBulkOrderForm === true) {
        const discountPct = AppConstants.BULK_ORDER_DISCOUNT_PCT;
        totalPrice = Object.keys(values.sizeQuantity).map(s => {
            const quantity = values.sizeQuantity[s];
            const perPrice = (product.price + productSizeOptions[s].priceAddition) * (1 - discountPct);

            return quantity * perPrice;
        }).reduce((p1, p2) => {
            return p1 + p2;
        }, 0);
        
    } else {
        const perPrice = product.price + (values.productSize? productSizeOptions[values.productSize].priceAddition: 0);
        totalPrice = (values.quantity && values.quantity > 0)? (perPrice * values.quantity): perPrice;
    }
    

    const locationExtraCost = values.locationType? AppUtil.getLocationExtraCost(values.locationType, product): 0;
    const deliveryTypeExtraCost = values.deliveryType? DELIVERY_TYPE[values.deliveryType].extraCost: 0;
    const productVariants = product.productVariants;

    let variantPriceAddition = 0;
    if(productVariants) {
        productVariants.forEach((v) => {
            const variantKey = ProductUtil.getProductVariantKey(v);
            const selectedVariantValue = values[variantKey];

            const priceAddition = v.variants.filter(v => v.variantId === selectedVariantValue)[0].priceAddition || 0;
            variantPriceAddition += priceAddition;
        });
    }

    const totalExtraCost = locationExtraCost + deliveryTypeExtraCost + variantPriceAddition;

    

    const totalCharge = (totalPrice + totalExtraCost) - discountAmount;
    return <React.Fragment>
            <h2 className="totalCost">
                {
                    showPriceOnly !== true &&
                    <span>Total Cost </span>
                }
                
                <span>Rs. {AppUtil.formatNumber(totalCharge)}</span>
        </h2>
    </React.Fragment>
};

export const ErrorMessages = () => {
    const {errors, touched} = useContext(OrderContext);

    const errorMessages = Object.keys(errors).filter(k => touched[k] === true)
													.map(k => <span className="errorMessage" key={"errror-"+k}><i className="fa fa-exclamation-circle" aria-hidden="true"></i> {errors[k]}</span>);
    return <React.Fragment>
            {errorMessages.length > 0 && 
                <div className="errorMessageDiv">
                    <Separator />
                    <h3>Please scroll up and fix below errors: </h3>
                    {errorMessages}
                </div>
            }
    </React.Fragment>
};

export const SubmitBtn = ({displayDeliveryInputs}) => {
    const {isSubmitting, values} = useContext(OrderContext);

    let submitBtn = "Buy Now";
    
    if(isSubmitting === true) {
        if(values.hasCouponCode)
            submitBtn = <span>Validating Coupon<i className="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i></span>;
    } else {
        if(values.hasCouponCode) submitBtn = "Apply Coupon"
        else if (displayDeliveryInputs !== true) submitBtn = "Next";
    }

    return <React.Fragment>
        <SubmitButton color="primary" disabled={isSubmitting} style={{width: '100%'}}>{submitBtn}</SubmitButton>
    </React.Fragment>
};

export const OrderInfo = ({prepaymentRequired}) => {
    const {product} = useContext(OrderContext);

    const isPhotoFrame = product.productType === AppConstants.PRODUCT_TYPES['PHOTO_FRAME'].val;
    const hasOutsideRingroadCharge = product.outsideRingRoadCharge || false;
    return <React.Fragment>
        <div>
            <ul style={{padding: '0', margin: '0 15px', color: '#596161', fontSize: '100%'}}>
                <li><b><Img src="/images/nepal flag.png" title="Nepal Flag" alt="Nepal Flag" style={{height: "12px", marginLeft: "3px", display: 'inline'}}/>  Made in Nepal</b></li>

                {/* {
                    !isPhotoFrame &&
                    <li style={{margin: '5px 0'}}><b>{hasOutsideRingroadCharge? '': 'Free '}Home Delivery Inside KTM Valley.</b></li>
                } */}
                

                {
                    prepaymentRequired !== true &&
                    <li style={{margin: '5px 0'}}><b><i className="fa fa-money" aria-hidden="true"></i> Cash On Delivery for Inside KTM Valley.</b></li>
                }
                
                <li style={{margin: '5px 0'}}>
                        <div className="flexColumn">
                            <b>Available Payment Options:</b>
                            <PaymentOptions />
                        </div>
                        
                </li>

                
            </ul>
            <hr />
        </div>
    </React.Fragment>
}



const styles = {
    headerStyle: {background: 'rgb(224 224 224 / 93%)', color: '#5f5d5d', padding: '5px 10px', textAlign: 'center'}
};

class OrderForm extends React.Component { 
    
    constructor(props) {
        super(props);
        this.validationScheme = this.validationScheme.bind(this);
		this.setSubmittedOrder = this.setSubmittedOrder.bind(this);
		this.saveFormData = this.saveFormData.bind(this);
        this.handleFormSubmission = this.handleFormSubmission.bind(this);
    }

    validationScheme() {
        const validation = this.props.validationScheme;
		return Yup.object().shape(validation);
    }

    setSubmittedOrder(setStatus, formValues, discountCoupon) {
		const orderReq = this.props.createOrderRequest(formValues);

        //Note: This overrides other status if any
        setStatus({submittedOrder: orderReq, discountCoupon: discountCoupon});
	}

    saveFormData(formValue) {
		CacheService.save(AppConstants.USER_ADDRESS_KEY, JSON.stringify({phone: formValue.phone, 
							streetAddress: formValue.streetAddress, 
							locationType: formValue.locationType}), true);
	}

    handleFormSubmission(value, {setSubmitting, setStatus}) {
		const couponCode = value.hasCouponCode? value.couponCode: null;

		if(couponCode) {
			OrderAPI.validateDiscountCoupon(couponCode, this.props.product.id, function(success, discountCoupon) {
				if(success) {
					this.setSubmittedOrder(setStatus, value, discountCoupon);
				} else {
					setSubmitting(false);
				}
			}.bind(this));
		} else {
			this.setSubmittedOrder(setStatus, value);
		}
	}

    render() {
        const product = this.props.product;

		if(product === null)
            return <OrderFormLoader />;

        const productSizes = AppUtil.getSizeInfo(product);

        return <Formik enableReinitialize initialValues={this.props.initialValues} onSubmit={this.handleFormSubmission}
        validationSchema={this.validationScheme}
        render={(formikProps) => {
            const values = formikProps.values;
            const status = formikProps.status;
            const submittedOrder = status && status.submittedOrder? status.submittedOrder: null;

            if(submittedOrder) {
                return <OrderConfirmation order={submittedOrder} product={product}
                        discountCoupon={status.discountCoupon} 
                        customerImage={values.customerUploadedImage}
                        onClose={(reset) => {
                            formikProps.setStatus({});//remove subittedOrder from status
                            formikProps.setSubmitting(false);

                            if(reset === true)
                                formikProps.resetForm();
                        }}/>
            }

            const prepaymentRequired = this.props.isPrePaymentRequired(values);

            const displayDeliveryInputs = this.props.displayDeliveryInputs(values);
            return <OrderContext.Provider value={{product: product, productSizes: productSizes,
                                                setBulkOrderFormType: (orderFormType) => {
                                                    formikProps.setFieldValue('isBulkOrderForm', orderFormType)
                                                    formikProps.setFieldValue('paymentPreference', "");
                                                },
                                                ...formikProps}}>

                    <div id="productPurchaseFormContainer">
                        <RGForm>
                            <div className="flexRow">
                                <div style={{flexGrow: "1", maxWidth: '500px', margin: '20px 5px 0 5px', height: '100%'}}>
                                    <OrderProductImage />
                                </div>

                                <div style={{flexGrow: "1", maxWidth: "500px"}} >
                                    <ProductInfo showCreator={true} />
                                    
                                    {this.props.render(values)}

                                    {
                                        displayDeliveryInputs &&

                                        <React.Fragment>
                                            <h2 style={styles.headerStyle}>Delivery Detail</h2>
                                            <LocationTypeInput prepaymentRequired={prepaymentRequired} />
                                            <AddressInput />

                                            <CouponCodeInput />

                                            <h2 style={styles.headerStyle}>Payment Preference</h2>
                                            <PaymentPreferenceInput prepaymentRequired={prepaymentRequired} />
                                            <TotalCost />
                                            <ErrorMessages />
                                            
                                        </React.Fragment>
                                    }
                                    <SubmitBtn displayDeliveryInputs={displayDeliveryInputs}/>
                                    <hr />
                                    
                                    <OrderInfo prepaymentRequired={prepaymentRequired} />
                                    <ProductSocialShare product={product} productImage={values.productImage}/>
                                </div>
                            </div>
                            
                        </RGForm>
                    </div>
                </OrderContext.Provider>
        }} />
    }
}

export default OrderForm;




// WEBPACK FOOTER //
// ./src/views/order/OrderForm.js